import { TurnFailurePayload, } from "@t3tools/contracts";
import * as Schema from "effect/Schema";
function isWorkEntry(entry) {
    return entry.kind === "work";
}
function isAssistantEntry(entry) {
    return entry.kind === "message" && entry.message.role === "assistant";
}
export function isLatestTurnSettled(latestTurn, session) {
    if (!latestTurn?.startedAt)
        return false;
    if (!latestTurn.completedAt)
        return false;
    if (!session)
        return true;
    if (session.orchestrationStatus === "running")
        return false;
    return true;
}
function entryTurnId(entry) {
    switch (entry.kind) {
        case "message":
            return entry.message.turnId ?? null;
        case "work":
            return entry.entry.turnId ?? null;
        case "turn-failed":
            return entry.turnId;
    }
}
export function splitTurnSegments(timelineEntries) {
    const segments = [];
    let current = null;
    const failures = [];
    for (const entry of timelineEntries) {
        if (entry.kind === "turn-failed") {
            failures.push({ entry: entry, preceding: current });
            continue;
        }
        if (entry.kind === "message" && entry.message.role === "user") {
            current = {
                userEntry: entry,
                turnId: entry.message.turnId ?? null,
                entries: [],
            };
            segments.push(current);
            continue;
        }
        const turnId = entryTurnId(entry);
        if (current === null ||
            (turnId !== null && current.turnId !== null && turnId !== current.turnId)) {
            current = { userEntry: null, turnId: null, entries: [] };
            segments.push(current);
        }
        if (turnId !== null && current.turnId === null) {
            current.turnId = turnId;
        }
        current.entries.push(entry);
    }
    for (const { entry, preceding } of failures) {
        const matching = entry.turnId !== null
            ? segments.findLast((segment) => segment.turnId === entry.turnId)
            : segments.find((segment) => entry.id === `turn-failed:${segment.userEntry?.message.id}`);
        const segment = matching ?? preceding;
        if (segment)
            segment.entries.push(entry);
        else
            segments.push({ userEntry: null, turnId: entry.turnId, entries: [entry] });
    }
    return segments;
}
function findTerminalAssistantIndex(entries) {
    for (let index = entries.length - 1; index >= 0; index -= 1) {
        const entry = entries[index];
        if (entry && isAssistantEntry(entry)) {
            return index;
        }
    }
    return -1;
}
/**
 * The turn's answer is the assistant message rendered outside the work group.
 * Messages that carry a phase decide it themselves: a question the agent asked
 * without pausing stays in the group; without a phase the last assistant
 * message is the answer once the turn settled, and while the turn is live it
 * stays the answer only until work follows it.
 */
export function collectAnswerMessageIds(entries, live) {
    const answerIds = new Set();
    const terminalIndex = findTerminalAssistantIndex(entries);
    const workAfterTerminal = terminalIndex !== -1 && entries.slice(terminalIndex + 1).some(isWorkEntry);
    entries.forEach((entry, index) => {
        if (!isAssistantEntry(entry))
            return;
        const { message } = entry;
        if (message.phase === "final_answer") {
            answerIds.add(message.id);
            return;
        }
        if (message.phase === "commentary" || message.phase === "question")
            return;
        if (index === terminalIndex && (!live || !workAfterTerminal)) {
            answerIds.add(message.id);
        }
    });
    return answerIds;
}
export function compareTimelineEntries(left, right) {
    if (left.sequence !== undefined && right.sequence !== undefined) {
        if (left.sequence !== right.sequence) {
            return left.sequence - right.sequence;
        }
    }
    else if (left.sequence !== undefined) {
        return 1;
    }
    else if (right.sequence !== undefined) {
        return -1;
    }
    return left.createdAt.localeCompare(right.createdAt) || left.id.localeCompare(right.id);
}
export function deriveTimelineEntries(messages, workEntries, activities = []) {
    const messageRows = messages.map((message) => ({
        id: message.id,
        kind: "message",
        createdAt: message.createdAt,
        ...(message.sequence !== undefined ? { sequence: message.sequence } : {}),
        message,
    }));
    const workRows = workEntries.map((entry) => ({
        id: entry.id,
        kind: "work",
        createdAt: entry.createdAt,
        ...(entry.sequence !== undefined ? { sequence: entry.sequence } : {}),
        entry,
    }));
    const failures = activities
        .filter((activity) => activity.kind === "turn.failed")
        .map((activity) => ({
        id: activity.id,
        kind: "turn-failed",
        createdAt: activity.createdAt,
        ...(activity.sequence !== undefined ? { sequence: activity.sequence } : {}),
        turnId: activity.turnId,
        reason: Schema.is(TurnFailurePayload)(activity.payload)
            ? activity.payload.reason
            : "agent_failed",
    }));
    return [...messageRows, ...workRows, ...failures].toSorted(compareTimelineEntries);
}
